import React from 'react';
import { Card, CardContent, CardHeader } from '../../../components/ui/card';
import { AlertCircle, Github } from 'lucide-react';

const GitHubIntegrationSection = () => {
  return (
    <div className="mt-12" data-section="github-integration">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">GitHub Integration</h2>
      
      <div className="space-y-8">
        <OverviewCard />
        <SetupGuideCard />
        <ConfigurationCard />
        <ManagementCard />
      </div>
    </div>
  );
};

const OverviewCard = () => (
  <Card>
    <CardContent className="p-6">
      <div className="flex items-center gap-3 mb-4">
        <Github className="w-8 h-8 text-gray-900" />
        <h3 className="text-lg font-semibold text-gray-900">Overview</h3>
      </div>
      <p className="text-gray-600 mb-4">
        The DeltaOps GitHub integration enables automatic changelog generation from your repository's commit history. 
        With a one-click installation process, you can start tracking changes across your selected repositories and branches.
      </p>
      <div className="bg-blue-50 p-4 rounded-lg">
        <h4 className="font-medium text-blue-900 mb-2">Key Features</h4>
        <ul className="list-disc list-inside space-y-1 text-blue-800">
          <li>One-click GitHub app installation</li>
          <li>Automatic ingestion of commit history (180 days by default)</li>
          <li>Flexible repository and branch selection</li>
          <li>Configurable history retention period</li>
        </ul>
      </div>
    </CardContent>
  </Card>
);

const SetupGuideCard = () => (
  <Card>
    <CardContent className="p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Setup Guide</h3>
      <ol className="space-y-4">
        <li className="flex items-start">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-3 mt-0.5">1</span>
          <div>
            <p className="text-gray-800 font-medium">Navigate to Integrations</p>
            <p className="text-gray-600">Go to https://console.deltaops.app/integrations</p>
          </div>
        </li>
        <li className="flex items-start">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-3 mt-0.5">2</span>
          <div>
            <p className="text-gray-800 font-medium">Select GitHub Integration</p>
            <p className="text-gray-600">Click on the GitHub option in the integrations list</p>
          </div>
        </li>
        <li className="flex items-start">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-3 mt-0.5">3</span>
          <div>
            <p className="text-gray-800 font-medium">Install GitHub App</p>
            <p className="text-gray-600">Click "Install" and follow the GitHub authorization flow</p>
          </div>
        </li>
        <li className="flex items-start">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-3 mt-0.5">4</span>
          <div>
            <p className="text-gray-800 font-medium">Select Repositories</p>
            <p className="text-gray-600">Choose which repositories you want to track</p>
          </div>
        </li>
      </ol>
    </CardContent>
  </Card>
);

const ConfigurationCard = () => (
  <Card>
    <CardContent className="p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Configuration Options</h3>
      <div className="space-y-6">
        <div>
          <h4 className="font-medium text-gray-900 mb-2">History Retention</h4>
          <p className="text-gray-600 mb-2">
            By default, the integration imports the last 180 days of commit history. 
            You can modify this setting in the configuration panel.
          </p>
        </div>
        
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Repository Settings</h4>
          <p className="text-gray-600 mb-2">
            For each repository, you can configure:
          </p>
          <ul className="list-disc list-inside space-y-1 text-gray-600 ml-4">
            <li>Tracked branches</li>
            <li>Commit history retention period</li>
            <li>Automatic changelog generation rules</li>
          </ul>
        </div>
      </div>
    </CardContent>
  </Card>
);

const ManagementCard = () => (
  <Card>
    <CardContent className="p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Managing Your Integration</h3>
      <div className="space-y-4">
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Updating Settings</h4>
          <p className="text-gray-600">
            To modify your integration settings:
          </p>
          <ol className="mt-2 space-y-2 text-gray-600">
            <li>1. Go to the Integrations page</li>
            <li>2. Find the GitHub integration</li>
            <li>3. Click "Configure" to adjust settings</li>
          </ol>
        </div>

        <div>
          <h4 className="font-medium text-gray-900 mb-2">Removing the Integration</h4>
          <p className="text-gray-600">
            To remove the GitHub integration:
          </p>
          <ol className="mt-2 space-y-2 text-gray-600">
            <li>1. Navigate to the Integrations page</li>
            <li>2. Locate the GitHub integration</li>
            <li>3. Click "Manage" to access removal options</li>
          </ol>
        </div>
      </div>
    </CardContent>
  </Card>
);

export default GitHubIntegrationSection;